/**
 * Formatação e conversão monetária — region-aware E empresa-aware.
 *
 * ── COMO A MOEDA DA EMPRESA SE PROPAGA ──────────────────────────────────────
 * Existem ~283 chamadas de formatPrice/formatPriceWithSymbol espalhadas neste
 * projeto. Editar todas para passar a moeda seria inviável e quebraria em cada
 * tela nova que alguém esquecesse.
 *
 * Em vez disso este módulo guarda um CONTEXTO AMBIENTE, setado uma vez por
 * sessão (login/refresh) e, no cardápio público, quando a loja é carregada.
 * As funções sem parâmetro de moeda leem esse contexto. Resultado: uma loja
 * libanesa configurar LBP faz todas as telas trocarem juntas, sem edição.
 *
 * Singleton de módulo é seguro AQUI (um usuário, uma empresa por aba) — mas é
 * proibido no backend, onde o mesmo processo atende várias empresas.
 *
 * ── NÃO-REGRESSÃO ───────────────────────────────────────────────────────────
 * Enquanto nenhuma empresa liga a multi-moeda (`habilitado: false`, que é o
 * default e o caso de 100% do Brasil), o contexto resolve para o regionConfig e
 * a saída é byte-a-byte a de antes desta feature.
 *
 * Espelhado em garcomapp-front/src/utils/currency.js e
 * backend-pedeja/utils/currency.js — mantenha os três em sincronia.
 */
import regionConfig from '../config/regionConfig';
import { getCurrencyMeta, isSupportedCurrency, roundToCurrency } from './currencyCatalog';

const CTX_REGIAO = {
  habilitado: false,
  base: regionConfig.currency.code,
  secundaria: null,
  exibicao: 'base',
  cotacao: null,
};

let _ctx = null;

/**
 * Traduz o objeto empresa em contexto monetário, com fallback duro para a região.
 * NUNCA lança e NUNCA devolve null.
 */
export function resolveMoneyCtx(empresa) {
  const cfg = empresa?.configuracoes?.moeda;
  if (!cfg || !cfg.habilitado) return CTX_REGIAO;
  if (!isSupportedCurrency(cfg.base)) return CTX_REGIAO;

  const querSecundaria = cfg.exibicao === 'secundaria' || cfg.exibicao === 'ambas';
  const secundariaOk =
    isSupportedCurrency(cfg.secundaria) &&
    cfg.secundaria !== cfg.base &&
    Number(cfg.cotacao) > 0;

  if (querSecundaria && !secundariaOk) {
    // Pediram exibição dupla mas a cotação não está utilizável. Degrada para só
    // a base em vez de exibir um número inventado ao cliente.
    return { habilitado: true, base: cfg.base, secundaria: null, exibicao: 'base', cotacao: null };
  }

  return {
    habilitado: true,
    base: cfg.base,
    secundaria: querSecundaria ? cfg.secundaria : null,
    exibicao: cfg.exibicao || 'base',
    cotacao: querSecundaria ? Number(cfg.cotacao) : null,
  };
}

/**
 * Define a empresa vigente. Chamar no login, no refresh de vínculo e no
 * carregamento do cardápio público (onde a empresa NÃO vem do localStorage).
 */
export function setEmpresaCurrency(empresa) {
  _ctx = resolveMoneyCtx(empresa);
  return _ctx;
}

/** Limpar no logout, senão a próxima empresa herda a moeda da anterior. */
export function clearEmpresaCurrency() {
  _ctx = null;
}

/** Contexto vigente. Se ninguém setou ainda, tenta o localStorage uma única vez. */
export function getMoneyCtx() {
  if (_ctx) return _ctx;
  try {
    const raw = localStorage.getItem('empresa');
    _ctx = resolveMoneyCtx(raw ? JSON.parse(raw) : null);
  } catch {
    _ctx = CTX_REGIAO;
  }
  return _ctx;
}

/**
 * Formata UM valor em UMA moeda, com símbolo.
 * As casas decimais saem do próprio Intl a partir do código da moeda — por isso
 * LBP sai "LBP 895,000" e USD sai "$10.00" sem nenhum if nosso.
 */
export function formatMoney(valor, currencyCode) {
  // No caminho legado o locale vem do regionConfig, NÃO do catálogo. Hoje os dois
  // coincidem, mas amarrar no regionConfig garante que o comportamento antigo
  // continue idêntico mesmo se um dia divergirem.
  const usaRegiao = !isSupportedCurrency(currencyCode);
  const code = usaRegiao ? regionConfig.currency.code : currencyCode;
  const locale = usaRegiao ? regionConfig.currency.locale : getCurrencyMeta(code).locale;
  const n = Number(valor) || 0;
  return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(n);
}

/** Converte da moeda base para a secundária, arredondando na granularidade do destino. */
export function convertMoney(valor, ctx) {
  const c = ctx || getMoneyCtx();
  if (!c?.secundaria || !(Number(c.cotacao) > 0)) return null;
  return roundToCurrency((Number(valor) || 0) * Number(c.cotacao), c.secundaria);
}

/**
 * Formata respeitando o modo de exibição da empresa.
 *
 * Devolve PARTES, não string colada, de propósito: o JSX precisa estilizar a
 * secundária menor/cinza, e a comanda térmica precisa decidir entre 1 e 2 linhas.
 *
 * @returns {{primary: string, secondary: string|null}}
 */
export function formatMoneyParts(valor, ctx) {
  const c = ctx || getMoneyCtx();

  if (!c?.habilitado) return { primary: formatMoney(valor), secondary: null };

  if (c.exibicao === 'secundaria' && c.secundaria) {
    return { primary: formatMoney(convertMoney(valor, c), c.secundaria), secondary: null };
  }

  const primary = formatMoney(valor, c.base);

  if (c.exibicao === 'ambas' && c.secundaria) {
    // O til marca aproximação — a moeda base é a que vale para cobrança.
    return { primary, secondary: `~${formatMoney(convertMoney(valor, c), c.secundaria)}` };
  }

  return { primary, secondary: null };
}

/**
 * Formata um número como preço SEM símbolo.
 * Sem `currencyCode`, usa a moeda base da empresa vigente (ou a da região).
 */
export function formatPrice(num, currencyCode) {
  const n = !num && num !== 0 ? 0 : Number(num);
  const code = isSupportedCurrency(currencyCode) ? currencyCode : getMoneyCtx().base;
  const usaRegiao = !isSupportedCurrency(code) || code === regionConfig.currency.code;
  const meta = getCurrencyMeta(code);

  return new Intl.NumberFormat(usaRegiao ? regionConfig.currency.locale : meta.locale, {
    minimumFractionDigits: meta.decimals,
    maximumFractionDigits: meta.decimals,
  }).format(n);
}

/**
 * Formata com símbolo. É a função que a maior parte do app já chama.
 *
 * O 2º parâmetro é opcional e aceita as duas formas:
 *  - um código de moeda ('LBP')
 *  - um contexto de `resolveMoneyCtx()`
 * Sem ele, usa o contexto ambiente (empresa vigente).
 */
export function formatPriceWithSymbol(num, ctxOrCode) {
  const n = !num && num !== 0 ? 0 : num;

  if (typeof ctxOrCode === 'string') return formatMoney(n, ctxOrCode);

  const { primary, secondary } = formatMoneyParts(n, ctxOrCode || undefined);
  return secondary ? `${primary} (${secondary})` : primary;
}

/**
 * Converte uma string formatada de volta para número.
 * Usa os separadores da moeda vigente, não os da região — em LBP não há decimal
 * e o separador de milhar é vírgula.
 */
export function convertPrice(priceStr, currencyCode) {
  if (!priceStr) return 0;
  if (typeof priceStr === 'number') return priceStr;

  const code = isSupportedCurrency(currencyCode) ? currencyCode : getMoneyCtx().base;
  const meta = getCurrencyMeta(code);

  let cleaned = String(priceStr).replace(meta.symbol, '').replace(/\s| /g, '').trim();

  if (meta.decimals === 0) {
    // Sem casas decimais: qualquer separador é de milhar.
    cleaned = cleaned.replace(/[.,]/g, '');
  } else if (meta.locale === 'pt-BR') {
    // 1.234,56 -> tira os pontos, vírgula vira ponto
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else {
    // 1,234.56 -> tira as vírgulas
    cleaned = cleaned.replace(/,/g, '');
  }

  return parseFloat(cleaned) || 0;
}

/** Símbolo da moeda vigente (ou de uma moeda específica). */
export function getCurrencySymbol(currencyCode) {
  const code = isSupportedCurrency(currencyCode) ? currencyCode : getMoneyCtx().base;
  if (code === regionConfig.currency.code) return regionConfig.currency.symbol;
  return getCurrencyMeta(code).symbol;
}


/**
 * Separador decimal da moeda vigente.
 *
 * Existe para os CAMPOS DE DIGITACAO. Para exibir valor use formatPrice, que
 * ja aplica separador de milhar -- num input o milhar atrapalha a digitacao.
 */
export function getDecimalSeparator(currencyCode) {
  const code = isSupportedCurrency(currencyCode) ? currencyCode : getMoneyCtx().base;
  const usaRegiao = !isSupportedCurrency(code) || code === regionConfig.currency.code;
  const locale = usaRegiao ? regionConfig.currency.locale : getCurrencyMeta(code).locale;
  return (1.1).toLocaleString(locale).replace(/\d/g, '');
}

/** Casas decimais da moeda vigente (LBP nao tem centavo: 0). */
export function getCurrencyDecimals(currencyCode) {
  const code = isSupportedCurrency(currencyCode) ? currencyCode : getMoneyCtx().base;
  return getCurrencyMeta(code).decimals;
}

/**
 * Formata para CAMPO DE DIGITACAO: casas e separador da moeda, sem milhar.
 * Em moeda sem centavo devolve o inteiro, sem separador nenhum.
 */
export function formatAmountForInput(num, currencyCode) {
  const n = !num && num !== 0 ? 0 : Number(num) || 0;
  const casas = getCurrencyDecimals(currencyCode);
  if (casas === 0) return String(Math.round(n));
  return n.toFixed(casas).replace('.', getDecimalSeparator(currencyCode));
}
/** Código da moeda vigente. */
export function getCurrencyCode() {
  return getMoneyCtx().base;
}
